import React from "react";

const BASIS_LABEL = {
  fixed: "Fixed project budget",
  hourly: "Hourly budget",
  daily: "Daily budget",
};

export function clientBudget(record = {}) {
  const snapshot = record.client_budget_basis_snapshot != null;
  const basis = snapshot ? record.client_budget_basis_snapshot : record.budget_basis;
  const amount = Number(snapshot ? record.client_budget_amount_snapshot : record.budget_amount);
  const unitsRaw = snapshot ? record.client_budget_units_snapshot : record.budget_units;
  const units = unitsRaw == null ? null : Number(unitsRaw);
  const currency = snapshot
    ? record.client_budget_currency_snapshot
    : record.currency;
  const storedTotal = snapshot
    ? record.client_budget_total_snapshot
    : record.budget_total;

  if (!basis || !currency || !Number.isFinite(amount) || amount <= 0) return null;
  if (basis !== "fixed" && (!Number.isFinite(units) || units <= 0)) return null;

  const total = Number.isFinite(Number(storedTotal))
    ? Number(storedTotal)
    : basis === "fixed" ? amount : Math.round(amount * units * 100) / 100;

  return { basis, amount, units, currency, total, snapshot };
}

export function money(amount, currency) {
  if (!Number.isFinite(Number(amount))) return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(amount));
  } catch {
    return `${currency || ""} ${Number(amount).toFixed(2)}`.trim();
  }
}

export function budgetSummary(record) {
  const budget = clientBudget(record);
  if (!budget) return "No client budget set";
  if (budget.basis === "fixed") {
    return `${BASIS_LABEL.fixed}: ${money(budget.total, budget.currency)}`;
  }
  const unitLabel = budget.basis === "hourly" ? "hour" : "day";
  return `${BASIS_LABEL[budget.basis]}: ${money(budget.amount, budget.currency)}/${unitLabel} × ${budget.units} ${unitLabel}${budget.units === 1 ? "" : "s"} = ${money(budget.total, budget.currency)}`;
}

export function ClientBudgetCard({ record, compact = false }) {
  const budget = clientBudget(record);
  if (!budget) return null;
  const unitLabel = budget.basis === "hourly" ? "hour" : "day";

  return (
    <div
      className={`rounded-xl border border-[var(--avcp-primary)] bg-[var(--avcp-primary-soft)] ${compact ? "p-3" : "p-5"}`}
      data-testid="client-budget-card"
    >
      <div className="text-xs font-semibold uppercase tracking-wide text-[var(--avcp-primary)]">
        Client target budget
      </div>
      <div className={`${compact ? "text-base" : "text-2xl"} mt-1 font-bold`}>
        {money(budget.total, budget.currency)}
      </div>
      <div className="mt-1 text-sm">
        {budget.basis === "fixed"
          ? "Fixed total for the project"
          : `${money(budget.amount, budget.currency)} per ${unitLabel} × ${budget.units} planned ${unitLabel}${budget.units === 1 ? "" : "s"}`}
      </div>
      {!compact && (
        <p className="mt-2 text-xs text-[var(--avcp-ink-soft)]">
          This is the client’s non-binding target budget. Accepted rates, approved time, invoices, and payouts remain authoritative.
        </p>
      )}
    </div>
  );
}
